import { z } from 'zod';

/**
 * The nine request types, and what each may point at.
 *
 * A fixed Project -> Requirement -> Location -> Scene chain cannot express these:
 * sourcing targets a REQUIREMENT, a recce targets a CANDIDATE, a permit question
 * targets a LOCATION and a SHOOTING_DAY. The alternative — one table with five
 * nullable foreign keys — turns every read into type-specific conditionals that
 * drift apart the first time a type is added.
 */
export const RequestType = z.enum([
  'FIND_LOCATION',
  'AVAILABILITY_CHECK',
  'MORE_PHOTOS',
  'INFORMATION',
  'TECHNICAL_FEASIBILITY',
  'RECCE',
  /** Named for what it IS. No canonical hold exists in this product, and calling
   *  it "Hold" would promise a booking action nothing performs. */
  'HOLD_COORDINATION',
  'PERMIT_QUESTION',
  'REPLACEMENT',
]);
export type RequestType = z.infer<typeof RequestType>;

export const RequestTargetType = z.enum([
  'REQUIREMENT',
  'CANDIDATE',
  'LOCATION',
  'SCENE',
  'SHOOTING_DAY',
  'BOOKING',
]);
export type RequestTargetType = z.infer<typeof RequestTargetType>;

/** One type can appear twice with different meanings — a SUBJECT location beside
 *  an ALTERNATIVE one — which is why the role is part of the key. */
export const RequestTargetRole = z.enum([
  'SUBJECT',
  'ALTERNATIVE',
  'REQUESTED_DATE',
  'RESULTING_BOOKING',
]);
export type RequestTargetRole = z.infer<typeof RequestTargetRole>;

export const RequestStatus = z.enum([
  'DRAFT',
  'SENT',
  'IN_PROGRESS',
  'SCHEDULED',
  'ANSWERED',
  'CANCELLED',
]);
export type RequestStatus = z.infer<typeof RequestStatus>;

export interface RequestTypeRule {
  readonly required: readonly RequestTargetType[];
  readonly allowed: readonly RequestTargetType[];
}

/**
 * The catalog. Required targets are what the request is meaningless without —
 * an availability check with no location is a question about nothing.
 */
export const REQUEST_TYPE_CATALOG: Readonly<Record<RequestType, RequestTypeRule>> = Object.freeze({
  FIND_LOCATION: { required: ['REQUIREMENT'], allowed: ['REQUIREMENT', 'SCENE'] },
  AVAILABILITY_CHECK: {
    required: ['LOCATION'],
    allowed: ['LOCATION', 'CANDIDATE', 'REQUIREMENT', 'SHOOTING_DAY'],
  },
  MORE_PHOTOS: { required: ['LOCATION'], allowed: ['LOCATION', 'CANDIDATE', 'REQUIREMENT'] },
  INFORMATION: {
    required: [],
    allowed: ['LOCATION', 'CANDIDATE', 'REQUIREMENT', 'SCENE', 'SHOOTING_DAY'],
  },
  TECHNICAL_FEASIBILITY: {
    required: ['LOCATION'],
    allowed: ['LOCATION', 'CANDIDATE', 'REQUIREMENT', 'SCENE'],
  },
  RECCE: { required: ['CANDIDATE'], allowed: ['CANDIDATE', 'LOCATION', 'SHOOTING_DAY'] },
  HOLD_COORDINATION: {
    required: ['LOCATION', 'SHOOTING_DAY'],
    allowed: ['LOCATION', 'CANDIDATE', 'SHOOTING_DAY', 'REQUIREMENT'],
  },
  PERMIT_QUESTION: {
    required: ['LOCATION'],
    allowed: ['LOCATION', 'SHOOTING_DAY', 'SCENE', 'REQUIREMENT'],
  },
  REPLACEMENT: {
    required: ['REQUIREMENT'],
    allowed: ['REQUIREMENT', 'CANDIDATE', 'LOCATION'],
  },
});

export interface TargetInput {
  readonly targetType: RequestTargetType;
  readonly targetRole: RequestTargetRole;
}

export interface CatalogViolation {
  readonly code: 'MISSING_REQUIRED_TARGET' | 'TARGET_TYPE_NOT_ALLOWED';
  readonly targetType: RequestTargetType;
}

/** Returns every violation, so one submission reports all its problems rather
 *  than one per round trip. */
export function validateRequestTargets(
  type: RequestType,
  targets: readonly TargetInput[],
): CatalogViolation[] {
  const rule = REQUEST_TYPE_CATALOG[type];
  const present = new Set(targets.map((t) => t.targetType));
  const violations: CatalogViolation[] = [];

  for (const required of rule.required) {
    if (!present.has(required)) {
      violations.push({ code: 'MISSING_REQUIRED_TARGET', targetType: required });
    }
  }

  for (const target of targets) {
    if (!rule.allowed.includes(target.targetType)) {
      violations.push({ code: 'TARGET_TYPE_NOT_ALLOWED', targetType: target.targetType });
    }
  }

  return violations;
}
